import { Suspense } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { withSSR } from "react-i18next";
import { Provider } from "react-redux";
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  matchRoutes,
} from "react-router-dom";

import { ThemeProvider } from "@emotion/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";

import { ApiService } from "./service/ApiService";

import { setupStore } from "./store/store";

import { getCookie } from "./utils/helpers";
import { theme } from "./utils/theme";

import { App } from "./App";
import { DirectionProvider } from "./DirectionProvider";
import FallbackUI from "./components/common/FallbackUI/FallbackUI";
import "./index.css";
import { routesFromElements } from "./router/router";
import { getRoutes } from "./router/routes";
import "./sentry";
import { appTheme } from "./theme";

const AppWithSentry = Sentry.withProfiler(App);

const ExtendedApp = withSSR()(AppWithSentry);

const rootEl = document.getElementById("root");

const getApp = async (initialState = {}) => {
  const store = setupStore(initialState);

  const langFromSsr =
    document.documentElement.lang === "#htmllang"
      ? ""
      : document.documentElement.lang;

  const initialLanguage = langFromSsr || getCookie("lang") || "en";

  const routes = createRoutesFromElements(
    getRoutes({
      lang: initialLanguage,
      store,
    }),
  );

  const router = createBrowserRouter(routes);

  let lazyMatches = matchRoutes(routesFromElements, window.location)?.filter(
    m => m.route.lazy,
  );

  if (lazyMatches && lazyMatches?.length > 0) {
    await Promise.all(
      lazyMatches.map(async m => {
        let routeModule = await m.route.lazy();
        Object.assign(m.route, { ...routeModule, lazy: undefined });
      }),
    );
  }

  return {
    app: (
      <Sentry.ErrorBoundary fallback={<FallbackUI />} showDialog>
        <DirectionProvider>
          <ThemeProvider theme={theme}>
            <MuiThemeProvider theme={appTheme}>
              <Provider store={store}>
                <HelmetProvider>
                  <Suspense fallback={null}>
                    <ExtendedApp
                      initialLanguage={initialLanguage}
                      routerProvider={RouterProvider}
                      routerProps={{
                        router,
                      }}
                    />
                  </Suspense>
                </HelmetProvider>
              </Provider>
            </MuiThemeProvider>
          </ThemeProvider>
        </DirectionProvider>
      </Sentry.ErrorBoundary>
    ),
  };
};

const optionRender = async (initialState = {}) => {
  const app = await getApp(initialState);

  if (rootEl.hasChildNodes()) {
    return hydrateRoot(rootEl, app.app, {
      identifierPrefix: "react-app-unlocklab",
    });
  } else {
    const root = createRoot(rootEl);

    return root.render(app.app);
  }
};

const initStore = async () => {
  try {
    const contentId = document.querySelector(
      `meta[name="ssr-content-id"]`,
    ).content;

    if (contentId) {
      const { data } = await ApiService.getCacheSSR(contentId);

      if (data.state) {
        return optionRender(data.state);
      }
    }

    return optionRender();
  } catch (e) {}
};

initStore();

// if (process.env.REACT_APP_MODE !== "dev") {
//   if ("serviceWorker" in navigator) {
//     window.addEventListener("load", () => {
//       const swUrl = `${process.env.REACT_APP_HOME_URL}/service-worker.js`;
//
//       navigator.serviceWorker.register(swUrl, { scope: "/" }).catch(error => {
//         console.error("Service worker registration failed:", error);
//       });
//     });
//   } else {
//     console.error("Service workers are not supported.");
//   }
// }
